import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Shield, Send, Loader2, AlertCircle, Clock } from "lucide-react";
import { useAdmin2FA, Admin2FAStatus } from "@/hooks/useAdmin2FA";

interface Admin2FAModalProps {
  isOpen: boolean;
  walletAddress: string;
  onVerified: () => void;
  onCancel: () => void;
}

export function Admin2FAModal({ isOpen, walletAddress, onVerified, onCancel }: Admin2FAModalProps) {
  const [code, setCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const { status, expiresAt, error, send2FACode, verify2FACode, reset } = useAdmin2FA();

  // Send code when modal opens
  useEffect(() => {
    if (isOpen && status === "idle") {
      send2FACode(walletAddress);
    }
  }, [isOpen, walletAddress, status, send2FACode]);

  // Countdown timer
  useEffect(() => {
    if (!expiresAt) return;

    const updateTimer = () => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((expiresAt.getTime() - now) / 1000));
      setTimeLeft(diff);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  // Auto-verify when 6 digits entered
  useEffect(() => {
    if (code.length === 6 && status === "pending") {
      handleVerify();
    }
  }, [code]);

  const handleVerify = async () => {
    const success = await verify2FACode(walletAddress, code);
    if (success) {
      onVerified();
    }
  };

  const handleResend = async () => {
    setCode("");
    reset();
    await send2FACode(walletAddress);
  };

  const handleClose = () => {
    reset();
    setCode("");
    onCancel();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Shield className="h-5 w-5 text-primary" />
            Admin 2FA Verification
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            A verification code has been sent to your Telegram.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-6 py-4">
          {status === "sending" && (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Sending verification code...</p>
            </div>
          )}

          {status === "pending" && (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Send className="h-4 w-4" />
                <span>Code sent to Telegram</span>
              </div>

              <InputOTP
                value={code}
                onChange={setCode}
                maxLength={6}
                disabled={status !== "pending"}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>

              {timeLeft > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>Code expires in {formatTime(timeLeft)}</span>
                </div>
              )}

              {timeLeft === 0 && expiresAt && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  <span>Code expired</span>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex gap-3 w-full">
                <Button
                  variant="outline"
                  onClick={handleResend}
                  disabled={timeLeft > 240}
                  className="flex-1"
                >
                  Resend Code
                </Button>
                <Button
                  onClick={handleVerify}
                  disabled={code.length !== 6}
                  className="flex-1"
                >
                  Verify
                </Button>
              </div>
            </>
          )}

          {status === "verifying" && (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Verifying code...</p>
            </div>
          )}

          {status === "error" && (
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
              <Button onClick={handleResend} variant="outline">
                Try Again
              </Button>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
